import { getFormattedFlowName } from 'utils/flow'
import { generateUUID } from 'utils/fpti'
import { getUCPConfig, serverData } from 'utils/server-data'
import { isMobileNative } from 'utils/mobile'
import { isTypingSpeedConfigRequired, FLOW_TO_TYPING_SPEED_CONFIG_MAP } from 'universal/fraudnet'

let CMID = ''

const setCmid = (f) => (CMID = f)

export const getCmid = () => CMID

const fnConfigScriptId = 'fnconfig'
const fnUrlScriptId = 'fnurl'
const noScriptId = 'fnoscript'

const fncls = (getUCPConfig as any)('fraudnet.fncls', '')
const scriptUrl = (getUCPConfig as any)('fraudnet.scriptUrl', '')
const noScriptUrl = (getUCPConfig as any)('fraudnet.noScriptUrl', '')

const removeDuplicatedScript = (id) => {
  const script = document.getElementById(id)
  if (script && script.parentNode) {
    script.parentNode.removeChild(script)
  }
}

const loadFraudnetConfig = (options) => {
  removeDuplicatedScript(fnConfigScriptId)

  const { clientMetadataId, flowName } = options

  const script = document.createElement('script')
  script.id = fnConfigScriptId
  script.type = 'application/json'
  script.setAttribute('fncls', fncls)
  script.text = JSON.stringify({
    f: clientMetadataId,
    s: flowName,
    ...(isTypingSpeedConfigRequired(flowName) && {
      ts: {
        type: flowName,
        fields: FLOW_TO_TYPING_SPEED_CONFIG_MAP[flowName],
      },
    }),
  })

  document.body.appendChild(script)
}

const loadFraudnetUrl = () => {
  removeDuplicatedScript(fnUrlScriptId)

  const script = document.createElement('script')
  script.id = fnUrlScriptId
  script.type = 'text/javascript'
  script.src = scriptUrl

  document.body.appendChild(script)
}

const loadNoScriptConfig = (options) => {
  removeDuplicatedScript(noScriptId)

  const { clientMetadataId, flowName } = options
  const noscript = document.createElement('noscript')
  noscript.id = noScriptId
  const img = document.createElement('img')
  img.src = `${noScriptUrl}?f=${clientMetadataId}&s=${flowName}&js=0&r=1`

  noscript.appendChild(img)
  document.body.appendChild(noscript)
}

const triggerDataCollection = () => {
  if (typeof window.PAYPAL?.asyncData?.initAndCollect === 'function') {
    window.PAYPAL.asyncData.initAndCollect()
  }
}

const isFraudnetScriptPresent = () => {
  const scriptElement = document.getElementById(fnUrlScriptId)
  return scriptElement
}

const injectConfigs = (options) => {
  loadFraudnetConfig(options)
  if (isFraudnetScriptPresent()) {
    triggerDataCollection()
    return
  }
  loadFraudnetUrl()
  loadNoScriptConfig(options)
}

// Since there are no page refreshes between modals (e.g., ActivateCardFlow to ReplaceCardFlow),
// the server HTML template will already be loaded with the Fraudnet scripts.
// We need to check if any existing flow-specific Fraudnet scripts are already loaded
// before triggering the Fraudnet call again.
const isFlowFraudnetConfigPresent = ({ flowName = '' } = {}) => {
  const scriptElement: any = document.getElementById(fnConfigScriptId) || {}
  const content = scriptElement.textContent || ''
  return !!(scriptElement && content && flowName && content.includes(flowName))
}

/**
 * (1) The cmId passed from native will be used only for the first data collection
 * (2) For subsequent data collection, a newly generated cmId will be used
 * There are scenarios where a user may move between multiple webview flows,
 * e.g., CardNotReceivedPage (Activate) -> ReportLostStolenPage (ReplaceCard) -> CardNotReceivedPage (Activate).
 * Since the noscript element will only be populated during the first render,
 * we can check the noscript element's src to determine if the cmId from native has already been extracted.
 */
const isMagnesIdExtracted = (magnesId) => {
  const noScriptElement = document.getElementById(noScriptId)
  const imgElement = noScriptElement?.childNodes[0] as HTMLImageElement | undefined
  const imgContent = imgElement?.src || ''
  return imgContent.includes(magnesId)
}

export const resubmitFraudnetData = ({
  subFlowName = '',
  flowName = '',
  forceSubmit = false,
} = {}) => {
  const formattedFlowName = getFormattedFlowName(subFlowName, flowName)
  if (forceSubmit || !isFlowFraudnetConfigPresent({ flowName: formattedFlowName })) {
    let cmId = generateUUID()

    const magnesId = serverData.magnesId
    if (isMobileNative() && magnesId && !isMagnesIdExtracted(magnesId)) {
      cmId = magnesId
    }

    setCmid(cmId)

    const options = { clientMetadataId: cmId, flowName: formattedFlowName }
    injectConfigs(options)
  }
}
